import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints, type MarketEndpoints } from '@/api/endpointCache'
import { LIVE_MAX_LIMIT } from '@/features/market/api/marketApi'
import { backendSectorKey, displayDate, sectorSlug } from '@/features/market/api/marketMappers'
import {
  DEPTH_LEVELS,
  depthBookSchema,
  depthCompanySchema,
  depthPageSchema,
  depthSectorSchema,
  type DepthBook,
  type DepthCompany,
  type DepthPage,
  type DepthQuery,
  type DepthSector,
} from '@/schemas/depth'

type Quote = Awaited<ReturnType<MarketEndpoints['live']>>['rows'][number]
type WireBook = Awaited<ReturnType<MarketEndpoints['depth']>>

/**
 * A sector's names, A→Z by symbol — the one list the books page through and
 * the company picker offers.
 *
 * mm-be lists a sector's members only on the heatmap's drill-down, and puts
 * them in no order: the rows come back as the database holds them, which
 * moves as prices are written. Paged in that order, page 2 could repeat or
 * skip a book from page 1.
 */
const sectorNames = async (api: MarketEndpoints, sectorId: string): Promise<DepthCompany[]> => {
  const names = await api.heatmap(backendSectorKey(sectorId))
  return names.rows
    .map((row) => ({ symbol: row.key, name: row.name }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
}

const toBook = (book: WireBook, quote: Quote | undefined) => {
  // The ladder's own ceiling, not the wire's: upstream sets the length. The
  // order totals below still count every rung it sent.
  const bids = book.buy
    .slice(0, DEPTH_LEVELS)
    .map((level) => ({ price: level.price, kitta: level.quantity }))
  const asks = book.sell
    .slice(0, DEPTH_LEVELS)
    .map((level) => ({ price: level.price, kitta: level.quantity }))
  return depthBookSchema.parse({
    symbol: book.symbol,
    name: book.name,
    // The live snapshot's LTP first, the book's own best rungs next — both
    // are real prices for this symbol. Only when neither exists is the price
    // genuinely unreported.
    ltp: quote?.ltp ?? bids[0]?.price ?? asks[0]?.price ?? null,
    percentChange: quote?.change_percent ?? null,
    bids,
    asks,
    bidTotal: {
      kitta: book.total_buy_qty,
      orders: book.buy.reduce((total, level) => total + level.order_count, 0),
    },
    askTotal: {
      kitta: book.total_sell_qty,
      orders: book.sell.reduce((total, level) => total + level.order_count, 0),
    },
    ratioCaption: book.message,
  })
}

export const depthService = {
  depth: async (query: DepthQuery, client?: QueryClient): Promise<DepthPage> => {
    const api = marketEndpoints(client)
    // The books need only the symbol list — from the URL when it names some,
    // otherwise the sector's names. So they start the moment that list
    // exists, not after `live` and `summary` too: those two only decorate the
    // books (LTP, % change, the date line) and run alongside, merged at the end.
    // The sector's names are not requested at all when the URL already names symbols.
    const allSymbolsRequest =
      query.symbols.length > 0
        ? Promise.resolve(query.symbols)
        : sectorNames(api, query.sector).then((names) => names.map((name) => name.symbol))
    const booksRequest = allSymbolsRequest.then(async (allSymbols) => {
      const start = (query.page - 1) * query.pageSize
      const symbols = allSymbols.slice(start, start + query.pageSize)
      const books = await Promise.all(symbols.map((symbol) => api.depth(symbol)))
      return { allSymbols, books }
    })
    const [{ allSymbols, books }, live, summary] = await Promise.all([
      booksRequest,
      api.live(LIVE_MAX_LIMIT),
      api.summary(),
    ])
    const quoteBySymbol = new Map(live.rows.map((row) => [row.key, row]))
    // Keyed on the symbol ASKED for, as before — not `book.symbol`.
    const rows: DepthBook[] = books.map((book, index) => {
      const symbol = allSymbols[(query.page - 1) * query.pageSize + index]
      return toBook(book, symbol === undefined ? undefined : quoteBySymbol.get(symbol))
    })

    return depthPageSchema.parse({
      rows,
      page: query.page,
      pageSize: query.pageSize,
      total: allSymbols.length,
      intro: 'The best published buy and sell orders for each selected company.',
      footnote: 'Depth changes as orders enter, execute, or are cancelled.',
      asOf: displayDate(summary.as_of),
      mitraSays: 'Market depth shows current orders, not guaranteed future trades.',
    })
  },

  sectors: async (client?: QueryClient): Promise<DepthSector[]> => {
    const sectors = await marketEndpoints(client).sectors('az')
    return depthSectorSchema
      .array()
      .parse(sectors.rows.map((sector) => ({ id: sectorSlug(sector.key), name: sector.name })))
  },

  /** One sector's names, for the company picker — see `sectorNames`. */
  sectorCompanies: async (sectorId: string, client?: QueryClient): Promise<DepthCompany[]> =>
    depthCompanySchema.array().parse(await sectorNames(marketEndpoints(client), sectorId)),
}
